import {
    Box,
    Checkbox,
    Divider,
    Drawer,
    FormControlLabel, IconButton, InputBase,
    List,
    ListItem,
    ListItemText,
    Typography,
    Grid
} from "@mui/material";
import React, {useEffect, useState, useMemo, useCallback} from "react";
import theme from "../../../../Shared/ThemeOld/dark";
import {LoadingButton} from "@mui/lab";
import {ACard} from "@atiautomacao/ati-ui-library";
import SearchIcon from "@mui/icons-material/Search";
import {AuthorityByUserIdReactQuery, UpdateAuthorityGroupReactQuery} from "../UsersReactQuery";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../../Config/Constants";
import {useAppSelector} from "../../../../Config/Hooks";
import {sharedQueryClient as queryClient} from "../../../../queryClient"

export interface User {
    id: number;
    name: string;
}
interface ParamsModalProps {
    open: boolean;
    onCloseModal: any;
    initialPermissions: Permission[];
    selectedPermissions: (list: Permission[]) => void
    user: User;
}

export interface Permission {
    id: number;
    name: string;
    checked: boolean;
}

const PermissionList = ({ permissions, totalItems, totalSelected, onToggle, onSelectAll }: { permissions: Permission[], totalItems: number, totalSelected: number,onToggle: (id: number) => void, onSelectAll: (checked: boolean) => void  }) => {
    const isAllSelected = totalItems > 0 && totalSelected === totalItems;
    const ordenedPermissions = [...permissions].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base'
        })
    );
    return (
        <Box sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: '#f8faff',
            borderRadius: 2,
            p: 1,
            maxHeight: 'calc(100vh - 400px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '8px'},
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#A7A8AC', borderRadius: '10px' }
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pl: 2, pr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000', fontSize: '12px', mt: 1 }}>
                    {totalSelected} <Typography component="span" variant="body2" sx={{fontSize: '12px'}}>de {totalItems} Perfis <br /> de acesso</Typography>
                </Typography>
                <FormControlLabel
                    checked={isAllSelected}
                    onChange={(_, checked) => onSelectAll(checked)}
                    control={<Checkbox size="small" />}
                    label={<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>Selecionar todas</Typography>}
                    labelPlacement="start"
                />
            </Box>

            <Divider sx={{ mb: 1, mr: 1, ml: 1 }} />
            <Box>
                <List sx={{ width: '100%', py: 0 }}>
                    {ordenedPermissions.map((item) => (
                        <ListItem
                            key={item.id}
                            onClick={() => onToggle(item.id)}
                            sx={{
                                py: 1.5,
                                px: 2,
                                cursor: 'pointer',
                                maxHeight: '50px',
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }
                            }}
                        >
                            <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: { fontWeight: 600, color: '#3c4043' }
                                }}
                            />
                            <Checkbox
                                size={"small"}
                                edge="end"
                                checked={item.checked}
                                disableRipple
                                sx={{
                                    color: '#6E6E70',
                                    '&.Mui-checked': {
                                        color: '#3b71ca',
                                    },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

export const SideBar = ({open, onCloseModal, initialPermissions, user, selectedPermissions}: ParamsModalProps) => {
    const account = useAppSelector(state => state.authentication.account);
    const [searchQuery, setSearchQuery] = useState('');
    const mutation = UpdateAuthorityGroupReactQuery();
    const {data} = AuthorityByUserIdReactQuery(user.id);
    const { enqueueSnackbar } = useSnackbar();
    const [permissions, setPermissions] = useState(initialPermissions ?? []);
    const isAuthorizedToManageUserAuthorityGroup = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.USER_AUTHORITY_GROUP_MANAGER
    ]);

    useEffect(() => {
        if(data && data.length > 0 && open){
            const newPermissions = initialPermissions.map(item => ({
                ...item,
                checked: data.some((d: any) => d.id === item.id)
            }));
            setPermissions(newPermissions)
        }

        if (open && (!data || data.length === 0)) {
            setPermissions(initialPermissions ?? []);
            setSearchQuery('');
        }
    }, [data, open, user.id]);

    useEffect(() => {
        selectedPermissions(filteredPermissions)
    }, [onCloseModal]);

    const filteredPermissions = useMemo(() => {
        const lowerCaseQuery = searchQuery.toLowerCase().trim();
        if (!lowerCaseQuery) return permissions;

        return permissions.filter(item =>
            item.name.toLowerCase().includes(lowerCaseQuery)
        );
    }, [searchQuery, permissions]);


    const onSubmit = () => {
        const authorityGroupList = permissions.filter(item => item.checked).map(item => item.id)
        if(authorityGroupList.length === 0){
            enqueueSnackbar("Selecione pelo menos uma permissão de acesso!", { variant: "warning", autoHideDuration: 2000});
            return;
        }
        console.log("userId", user.id)
        mutation.mutate({userId: user.id, authorityGroupList: authorityGroupList},{
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["find-authority-group-by-user-id", user.id]
                });
            }
        });
        setSearchQuery('')
        onCloseModal();
    }

    const handleToggle = useCallback((id: number) => {
        setPermissions(prev =>
            prev.map(item => item.id === id ? { ...item, checked: !item.checked } : item)
        );
    }, []);

    const handleSelectAll = useCallback((checked: boolean) => {
        setPermissions(prev => prev.map(item => ({ ...item, checked })));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    return (
        <Drawer open={open} anchor="right"
                PaperProps={{sx: {width: '418px', mt: '60px', backgroundColor: theme.palette.background.default}}}
                onClose={onCloseModal} aria-labelledby="drawer-sidebar-title"
                aria-describedby="modal-modal-description"
        >
            <ACard
                title={"Associar Perfis de acesso"}
                styleProps={{
                    cardStyle: {padding: 0},
                    headerStyle: { margin: 0, padding: '14px 18px 0px 18px'},
                    titleStyle: {fontSize: 26, fontWeight: 500,  letterSpacing: '-0.04rem', padding: 0, margin: 0},
                    subTitleStyle: {padding: 0, marginBottom: -25}
                }}
                footerActions={
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, mb: 2, padding: '0 24px' }}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            loading={false}
                            onClick={onSubmit}
                            disabled={!isAuthorizedToManageUserAuthorityGroup}
                            sx={{
                                boxShadow: 'none',
                                borderRadius: '10px',
                                p: 1,
                                fontSize: '15px'
                            }}
                        >
                            Associar perfis de acesso
                        </LoadingButton>
                        <LoadingButton variant="outlined" color="secondary" onClick={onCloseModal} loading={false} sx={{border: 0, fontWeight: 600,borderRadius: '10px',
                            p: 1,
                            fontSize: '15px', '&:hover': {borderRadius: '10px', border: 0, fontWeight: 600, p: 1,
                                fontSize: '15px'}}}>
                            Cancelar
                        </LoadingButton>
                    </Box>
                }
            >
                <Grid container spacing={0} sx={{ padding: 0, margin: 1 }}>
                    <Grid item xs={12} sx={{marginRight: 2}}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingY: 2,
                                borderTop: '1px solid #e0e0e0',
                                borderBottom: '1px solid #e0e0e0',
                                mb: 2,

                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '0.9rem',
                                }}
                            >
                                Usuários selecionados
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#3c4043',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                }}
                            >
                                {user.name}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #ccc',
                            borderRadius: "4px",
                            margin: 0,
                            marginRight: 2,
                            padding: "2px",
                            backgroundColor: '#fff'
                        }}>
                            <InputBase
                                value={searchQuery}
                                onChange={handleChange}
                                sx={{ ml: 1, flex: 1 }}
                                placeholder="Buscar"
                            />
                            <IconButton sx={{ p: '10px', cursor: 'default', pointerEvents: 'none' }}>
                                <SearchIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{marginTop: 2, marginRight: 2,}}>
                        <PermissionList
                            totalSelected={permissions.filter(p => p.checked).length}
                            totalItems={permissions.length}
                            permissions={filteredPermissions}
                            onToggle={handleToggle}
                            onSelectAll={handleSelectAll}/>
                    </Grid>
                </Grid>
            </ACard>
        </Drawer>
    )
}